import { Root } from '@radix-ui/react-slot';
import { cn } from '@utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const textVariants = cva('leading-7', {
	variants: {
		size: {
			xs: 'text-xs',
			sm: 'text-sm',
			md: 'text-base',
			lg: 'text-lg',
			xl: 'text-xl',
		},
		weight: {
			normal: 'font-normal',
			medium: 'font-medium',
			bold: 'font-semibold',
		},
		color: {
			primary: 'text-primary',
			secondary: 'text-secondary',
			black: 'text-textBlack',
			darkGray: 'text-textDarkGray',
			mediumGray: 'text-textMediumGray',
			success: 'text-success',
			accent: 'text-accent',
			disabled: 'text-disabled',
			error: 'text-error',
			white: 'text-white',
			info: 'text-info',
		},
		truncate: {
			true: 'truncate',
			false: '',
		},
	},
	defaultVariants: {
		weight: 'normal',
		color: 'black',
		size: 'md',
		truncate: false,
	},
});

export interface TextProps
	extends Omit<React.HTMLAttributes<HTMLParagraphElement>, 'color'>,
		VariantProps<typeof textVariants> {
	as?: 'p' | 'span' | 'div';
	asChild?: boolean;
}

const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
	({ className, weight, color, truncate, as: Tag = 'span', asChild, size, children, ...props }, ref) => {
		const Comp = asChild ? (Root as React.ElementType) : Tag;
		return (
			<Comp
				ref={ref}
				className={cn(
					textVariants({
						weight,
						color,
						truncate,
						size,
					}),
					className
				)}
				{...props}
			>
				{children}
			</Comp>
		);
	}
);

Text.displayName = 'Text';

export { Text };
